
import { useEffect, useRef, useState, useContext } from "react";
import HearingRoundedIcon from "@mui/icons-material/HearingRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { sendAssistantVoiceChatStreamMessage } from "./../../services/aiVoiceAssistantApi";
import { useAuth } from "../../hooks/AuthProvider";
import { LanguageContext } from "../../context/LanguageContext";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import "./VoiceAssistant.css";
const CONVERSATION_STORAGE_PREFIX =
  "sama-ai-voice-conversation";

const BROWSER_VOICE_STORAGE_KEY =
  "sama-browser-voice-command-settings";

const PRE_ROLL_MS = 10000;
const RECORDING_TIMESLICE_MS = 250;
const COMMAND_FINISH_DELAY_MS = 1500;
const WAKE_RESTART_DELAY_MS = 300;
const WAKE_RETRY_DELAY_MS = 500;

// =========================================================
// Session helpers
// =========================================================

const normalizeChatSessionId = (value) => {
  const normalized = String(value || "").trim();

  return normalized && normalized.length <= 255
    ? normalized
    : "";
};

const createUuid = () => {
  if (
    typeof window !== "undefined" &&
    window.crypto?.randomUUID
  ) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (char) => {
      const rand = Math.floor(Math.random() * 16);

      const value =
        char === "x"
          ? rand
          : (rand & 0x3) | 0x8;

      return value.toString(16);
    }
  );
};

const getConversationStorageKey = (
  userId,
  organizationId
) => {
  const safeUserId =
    userId || "anonymous";

  const safeOrgId =
    organizationId || "no-organization";

  return `${CONVERSATION_STORAGE_PREFIX}:${safeUserId}:${safeOrgId}`;
};

const readConversation = (storageKey) => {
  if (
    typeof window === "undefined" ||
    !storageKey
  ) {
    return {
      key: storageKey,
      sessionId: "",
    };
  }

  try {
    const raw =
      window.sessionStorage.getItem(
        storageKey
      );

    if (!raw) {
      return {
        key: storageKey,
        sessionId: "",
      };
    }

    const parsed =
      JSON.parse(raw);

    const sharedSessionId =
      normalizeChatSessionId(
        parsed?.chatSessionId
      ) ||
      normalizeChatSessionId(
        parsed?.sessionId
      );

    return {
      key: storageKey,
      sessionId:
        sharedSessionId,
    };
  } catch (error) {
    console.error(
      "Failed to read AI assistant conversation:",
      error
    );

    return {
      key: storageKey,
      sessionId: "",
    };
  }
};

// =========================================================
// Browser voice setting
// =========================================================

const readBrowserVoiceEnabled = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const raw =
      window.localStorage.getItem(
        BROWSER_VOICE_STORAGE_KEY
      );

    if (!raw) {
      return false;
    }

    const parsed =
      JSON.parse(raw);

    return parsed?.enabled === true;
  } catch (error) {
    console.error(
      "Failed to read browser voice command setting:",
      error
    );

    return false;
  }
};

// =========================================================
// Component
// =========================================================

export default function VoiceAssistant() {
  // =========================================================
  // Wake recognition
  // =========================================================

  const recognitionRef =
    useRef(null);

  const isRunningRef =
    useRef(false);

  const shouldRunRef =
    useRef(false);

  const wakeRestartTimerRef =
    useRef(null);

  const wakeRetryTimerRef =
    useRef(null);

  const restartWakeRecognitionRef =
    useRef(null);

  // Used to invalidate async microphone startup
  const recorderGenerationRef =
    useRef(0);

  // =========================================================
  // Continuous MediaRecorder
  // =========================================================

  const continuousRecorderRef =
    useRef(null);

  const continuousStreamRef =
    useRef(null);

  const startingRecorderPromiseRef =
    useRef(null);

  // First WebM initialization chunk
  const firstAudioChunkRef =
    useRef(null);

  // Last 10 seconds
  const preRollChunksRef =
    useRef([]);

  // Current command
  const commandAudioChunksRef =
    useRef([]);

  // Used when requestData() is called
  const flushResolveRef =
    useRef(null);

  const flushTimeoutRef =
    useRef(null);

  // =========================================================
  // Command state
  // =========================================================

  const isCommandModeRef =
    useRef(false);

  const isProcessingCommandRef =
    useRef(false);

  const shouldSendCommandRef =
    useRef(false);

  const commandHasAnySpeechRef =
    useRef(false);

  const commandHasFinalResultRef =
    useRef(false);

  const commandFinishTimerRef =
    useRef(null);

  const commandStartedAtRef =
    useRef(null);

  const lastTriggerRef =
    useRef(0);

  const speechBufferRef =
    useRef("");

  // Prevent played audio from entering pre-roll
  const assistantSpeakingRef =
    useRef(false);

  // =========================================================
  // Dynamic values
  // =========================================================

  const sessionIdRef =
    useRef("");

  const organizationIdRef =
    useRef(null);

  const subscriberIdRef =
    useRef(null);

  const langRef =
    useRef("en");

  const conversationKeyRef =
    useRef("");

  // =========================================================
  // UI
  // =========================================================

  const [isListening, setIsListening] =
    useState(false);

  const [isThinking, setIsThinking] =
    useState(false);

  const [conversation, setConversation] =
    useState({
      key: "",
      sessionId: "",
    });

  // =========================================================
  // Auth / language
  // =========================================================

  const { org, user } =
    useAuth();

  const { lang } =
    useContext(LanguageContext);

  const organizationId =
    org?.id;

  const subscriberId =
    user?.subid || org?.subid;

  const conversationKey =
    getConversationStorageKey(
      user?.id,
      organizationId
    );

  // =========================================================
  // Update refs
  // =========================================================

  useEffect(() => {
    organizationIdRef.current =
      organizationId;
  }, [organizationId]);

  useEffect(() => {
    subscriberIdRef.current =
      subscriberId;
  }, [subscriberId]);

  useEffect(() => {
    langRef.current =
      lang;
  }, [lang]);

  useEffect(() => {
    conversationKeyRef.current =
      conversationKey;
  }, [conversationKey]);

  // =========================================================
  // Load conversation
  // =========================================================

  useEffect(() => {
    const nextConversation =
      readConversation(
        conversationKey
      );

    setConversation(
      nextConversation
    );

    sessionIdRef.current =
      nextConversation.sessionId;
  }, [conversationKey]);

  // =========================================================
  // Current session id
  // =========================================================

  useEffect(() => {
    const nextSessionId =
      conversation.key ===
        conversationKey
        ? normalizeChatSessionId(
          conversation.sessionId
        )
        : "";

    sessionIdRef.current =
      nextSessionId;
  }, [
    conversation,
    conversationKey,
  ]);

  // =========================================================
  // Save conversation
  // =========================================================

  useEffect(() => {
    if (
      conversation.key !==
      conversationKey ||
      typeof window ===
      "undefined"
    ) {
      return;
    }

    try {
      const raw =
        window.sessionStorage.getItem(
          conversationKey
        );

      const existing =
        raw
          ? JSON.parse(raw)
          : {};

      window.sessionStorage.setItem(
        conversationKey,
        JSON.stringify({
          ...existing,
          sessionId:
            conversation.sessionId,
        })
      );
    } catch (error) {
      console.error(
        "Failed to save AI voice assistant conversation:",
        error
      );
    }
  }, [
    conversation,
    conversationKey,
  ]);

  // =========================================================
  // Hello words
  // =========================================================

  const helloWords = [
    "hello",
    "helo",
    "hallo",
    "hullo",
    "hillo",
    "holla",
    "holly",
    "hollow",
    "hallow",
    "halo",
    "hey",
    "hay",
    "heya",
    "heyya",
    "heyy",
    "hey there",
    "hi",
    "hai",
    "hy",
    "high",
    "hie",
    "hiya",
    "hiya there",
    "hi there",
    "hello there",
    "hello hi",
    "hey hi",
    "hey hello",
    "hi hello",
    "a",
    "is",
  ];

  // =========================================================
  // Sama words
  // =========================================================

  const samaWords = [
    "sama",
    "samaa",
    "samma",
    "samar",
    "samah",
    "samaah",
    "samantha",
    "sam",
    "samo",
    "samu",
    "samuel",
    "simo",
    "simon",
    "simone",
    "sam a",
    "some a",
    "soma",
    "somer",
    "summer",
    "someone",
    "somehow",
    "sema",
    "semma",
    "semih",
    "semmel",
    "simmel",
    "samae",
    "semantic",
    "semantics",
    "semitic",
    "somebody",
    "sama ai",
    "sama iot",
    "sama system",
    "sir",
    "isomil",
    "sumo",
    "semant",
    "isomer",
  ];

  // =========================================================
  // Normalize
  // =========================================================

  const normalize = (text) => {
    return String(text || "")
      .toLowerCase()
      .replace(/[^a-z\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // =========================================================
  // Levenshtein
  // =========================================================

  const levenshtein = (
    a,
    b
  ) => {
    const matrix = [];

    for (
      let i = 0;
      i <= b.length;
      i++
    ) {
      matrix[i] = [i];
    }

    for (
      let j = 0;
      j <= a.length;
      j++
    ) {
      matrix[0][j] = j;
    }

    for (
      let i = 1;
      i <= b.length;
      i++
    ) {
      matrix[i][0] = i;
    }

    for (
      let i = 1;
      i <= b.length;
      i++
    ) {
      for (
        let j = 1;
        j <= a.length;
        j++
      ) {
        matrix[i][j] =
          b[i - 1] === a[j - 1]
            ? matrix[i - 1][j - 1]
            : Math.min(
              matrix[i - 1][j] + 1,
              matrix[i][j - 1] + 1,
              matrix[i - 1][j - 1] + 1
            );
      }
    }

    return matrix[b.length][a.length];
  };

  // =========================================================
  // Contains word
  // =========================================================

  const containsWord = (
    text,
    words
  ) => {
    const normalizedText =
      normalize(text);

    if (!normalizedText) {
      return false;
    }

    const textWords =
      normalizedText.split(" ");

    for (const word of words) {
      const normalizedWord =
        normalize(word);

      if (
        normalizedText ===
        normalizedWord ||
        normalizedText.includes(
          ` ${normalizedWord} `
        ) ||
        normalizedText.startsWith(
          `${normalizedWord} `
        ) ||
        normalizedText.endsWith(
          ` ${normalizedWord}`
        )
      ) {
        return true;
      }

      if (
        !normalizedWord.includes(
          " "
        )
      ) {
        for (const textWord of textWords) {
          if (
            textWord.length <
            2
          ) {
            continue;
          }

          const distance =
            levenshtein(
              textWord,
              normalizedWord
            );

          const allowedDistance =
            normalizedWord.length <=
              4
              ? 1
              : 2;

          if (
            distance <=
            allowedDistance
          ) {
            return true;
          }
        }
      }
    }

    return false;
  };

  // =========================================================
  // Detect wake word
  // =========================================================

  const detectWakeWord = (
    text
  ) => {
    const normalized =
      normalize(text);

    if (!normalized) {
      return false;
    }

    const hasHello =
      containsWord(
        normalized,
        helloWords
      );

    const hasSama =
      containsWord(
        normalized,
        samaWords
      );

    console.log(
      "🔎 Detection:",
      {
        text: normalized,
        hasHello,
        hasSama,
      }
    );

    return (
      hasHello &&
      hasSama
    );
  };

  // =========================================================
  // Clear pre-roll
  // =========================================================

  const clearPreRollBuffer = () => {
    preRollChunksRef.current =
      [];
  };

  // =========================================================
  // Get last 10 seconds
  // =========================================================

  const getPreRollChunks = () => {
    const cutoff =
      Date.now() - PRE_ROLL_MS;

    return preRollChunksRef.current.filter(
      (item) =>
        item.timestamp >= cutoff
    );
  };

  // =========================================================
  // Flush current MediaRecorder data
  // =========================================================

  const flushRecorderData = async () => {
    const recorder =
      continuousRecorderRef.current;

    if (
      !recorder ||
      recorder.state !==
      "recording"
    ) {
      return;
    }

    if (
      flushResolveRef.current
    ) {
      return;
    }

    await new Promise(
      (resolve) => {
        let done =
          false;

        const finish =
          () => {
            if (done) {
              return;
            }

            done = true;

            if (
              flushTimeoutRef.current
            ) {
              clearTimeout(
                flushTimeoutRef.current
              );

              flushTimeoutRef.current =
                null;
            }

            flushResolveRef.current =
              null;

            resolve();
          };

        flushResolveRef.current =
          finish;

        flushTimeoutRef.current =
          setTimeout(
            finish,
            700
          );

        try {
          recorder.requestData();
        } catch (error) {
          console.warn(
            "⚠️ requestData failed:",
            error
          );

          finish();
        }
      }
    );
  };

  // =========================================================
  // Start continuous MediaRecorder
  // =========================================================

  const startContinuousRecording =
    async () => {
      if (
        !shouldRunRef.current
      ) {
        return false;
      }

      if (
        continuousRecorderRef.current &&
        continuousRecorderRef.current.state ===
        "recording"
      ) {
        return true;
      }

      // Another startup already running
      if (
        startingRecorderPromiseRef.current
      ) {
        return await startingRecorderPromiseRef.current;
      }

      const generation =
        ++recorderGenerationRef.current;

      const startPromise =
        (async () => {
          try {
            console.log(
              "🎙️ Starting continuous MediaRecorder..."
            );

            const stream =
              await navigator.mediaDevices.getUserMedia({
                audio: true,
              });

            // ==================================================
            // IMPORTANT:
            // Check generation after async getUserMedia.
            // This prevents old React effects from stealing
            // the microphone after a remount.
            // ==================================================

            if (
              generation !==
              recorderGenerationRef.current ||
              !shouldRunRef.current
            ) {
              console.log(
                "🛑 Ignoring obsolete microphone startup"
              );

              stream
                .getTracks()
                .forEach(
                  (track) => {
                    try {
                      track.stop();
                    } catch { }
                  }
                );

              return false;
            }

            continuousStreamRef.current =
              stream;

            const mimeType =
              MediaRecorder.isTypeSupported(
                "audio/webm;codecs=opus"
              )
                ? "audio/webm;codecs=opus"
                : "audio/webm";

            const recorder =
              new MediaRecorder(
                stream,
                {
                  mimeType,
                }
              );

            firstAudioChunkRef.current =
              null;

            clearPreRollBuffer();

            commandAudioChunksRef.current =
              [];

            continuousRecorderRef.current =
              recorder;

            recorder.onstart =
              () => {
                console.log(
                  "✅ Continuous MediaRecorder started"
                );
              };

            recorder.ondataavailable =
              (event) => {
                if (
                  !event.data ||
                  event.data.size ===
                  0
                ) {
                  return;
                }

                // ==========================================
                // FIRST CHUNK = WEBM INITIALIZATION
                // ==========================================

                if (
                  !firstAudioChunkRef.current
                ) {
                  firstAudioChunkRef.current =
                    event.data;

                  console.log(
                    "📦 WebM initialization chunk saved"
                  );

                  if (
                    flushResolveRef.current
                  ) {
                    flushResolveRef.current();
                  }

                  return;
                }

                const now =
                  Date.now();

                const chunk = {
                  timestamp:
                    now,

                  data:
                    event.data,
                };

                // ==========================================
                // Store pre-roll ONLY while not processing
                // or playing assistant audio.
                // ==========================================

                if (
                  !isProcessingCommandRef.current &&
                  !assistantSpeakingRef.current
                ) {
                  preRollChunksRef.current.push(
                    chunk
                  );

                  const cutoff =
                    now -
                    PRE_ROLL_MS;

                  preRollChunksRef.current =
                    preRollChunksRef.current.filter(
                      (item) =>
                        item.timestamp >=
                        cutoff
                    );
                }

                // ==========================================
                // COMMAND AUDIO
                //
                // Don't duplicate the chunk that was
                // already part of the pre-roll snapshot.
                // ==========================================

                if (
                  isCommandModeRef.current &&
                  commandStartedAtRef.current &&
                  now >
                  commandStartedAtRef.current
                ) {
                  commandAudioChunksRef.current.push(
                    chunk
                  );
                }

                // ==========================================
                // Resolve requestData()
                // ==========================================

                if (
                  flushResolveRef.current
                ) {
                  flushResolveRef.current();
                }
              };

            recorder.onerror =
              (event) => {
                console.error(
                  "❌ MediaRecorder error:",
                  event
                );
              };

            recorder.onstop =
              () => {
                console.log(
                  "🛑 Continuous MediaRecorder stopped"
                );

                if (
                  continuousRecorderRef.current ===
                  recorder
                ) {
                  continuousRecorderRef.current =
                    null;
                }
              };

            recorder.start(
              RECORDING_TIMESLICE_MS
            );

            return true;
          } catch (error) {
            console.error(
              "❌ Failed to start MediaRecorder:",
              error
            );

            continuousRecorderRef.current =
              null;

            if (
              continuousStreamRef.current
            ) {
              continuousStreamRef.current
                .getTracks()
                .forEach(
                  (track) => {
                    try {
                      track.stop();
                    } catch { }
                  }
                );
            }

            continuousStreamRef.current =
              null;

            firstAudioChunkRef.current =
              null;

            clearPreRollBuffer();

            return false;
          }
        })();

      startingRecorderPromiseRef.current =
        startPromise;

      try {
        return await startPromise;
      } finally {
        if (
          startingRecorderPromiseRef.current ===
          startPromise
        ) {
          startingRecorderPromiseRef.current =
            null;
        }
      }
    };

  // =========================================================
  // Stop continuous recorder
  // =========================================================

  const stopContinuousRecording =
    () => {
      // Invalidate any old async startup
      recorderGenerationRef.current++;

      // Allow a new startup after cleanup
      startingRecorderPromiseRef.current =
        null;

      const recorder =
        continuousRecorderRef.current;

      if (
        recorder &&
        recorder.state !==
        "inactive"
      ) {
        try {
          recorder.stop();
        } catch { }
      }

      continuousRecorderRef.current =
        null;

      if (
        continuousStreamRef.current
      ) {
        continuousStreamRef.current
          .getTracks()
          .forEach(
            (track) => {
              try {
                track.stop();
              } catch { }
            }
          );
      }

      continuousStreamRef.current =
        null;

      firstAudioChunkRef.current =
        null;

      clearPreRollBuffer();

      commandAudioChunksRef.current =
        [];

      if (
        flushTimeoutRef.current
      ) {
        clearTimeout(
          flushTimeoutRef.current
        );

        flushTimeoutRef.current =
          null;
      }

      flushResolveRef.current =
        null;
    };

  // =========================================================
  // Play recorded audio
  // =========================================================

  const playAudioBlob =
    async (audioBlob) => {
      if (
        !audioBlob ||
        !audioBlob.size
      ) {
        return;
      }

      const audioUrl =
        window.URL.createObjectURL(
          audioBlob
        );

      const audio =
        new window.Audio(
          audioUrl
        );

      audio.volume =
        1;

      audio.preload =
        "auto";

      assistantSpeakingRef.current =
        true;

      try {
        console.log(
          "🔊 Playing user's recorded audio..."
        );

        await new Promise(
          (resolve) => {
            let finished =
              false;

            const cleanup =
              () => {
                if (
                  finished
                ) {
                  return;
                }

                finished =
                  true;

                try {
                  audio.pause();
                } catch { }

                audio.removeAttribute(
                  "src"
                );

                audio.load();

                window.URL.revokeObjectURL(
                  audioUrl
                );

                resolve();
              };

            audio.onended =
              () => {
                console.log(
                  "✅ User recording playback finished"
                );

                cleanup();
              };

            audio.onerror =
              (error) => {
                console.warn(
                  "⚠️ User recording playback error:",
                  error
                );

                cleanup();
              };

            audio
              .play()
              .then(() => {
                console.log(
                  "🔊 User recording playback started"
                );
              })
              .catch(
                (error) => {
                  console.warn(
                    "⚠️ Could not play user's recorded voice:",
                    error
                  );

                  cleanup();
                }
              );
          }
        );
      } catch (error) {
        console.warn(
          "⚠️ Failed to play recorded audio:",
          error
        );
      } finally {
        assistantSpeakingRef.current =
          false;
      }
    };

  // =========================================================
  // Schedule command finish
  // =========================================================

  const scheduleCommandFinish =
    () => {
      if (
        commandFinishTimerRef.current
      ) {
        clearTimeout(
          commandFinishTimerRef.current
        );
      }

      commandFinishTimerRef.current =
        setTimeout(() => {
          commandFinishTimerRef.current =
            null;

          if (
            shouldRunRef.current &&
            isCommandModeRef.current &&
            !isProcessingCommandRef.current &&
            shouldSendCommandRef.current
          ) {
            finishCommandRecording();
          }
        }, COMMAND_FINISH_DELAY_MS);
    };

  // =========================================================
  // Finish command
  // =========================================================

  const finishCommandRecording =
    async () => {
      if (
        !shouldSendCommandRef.current ||
        isProcessingCommandRef.current
      ) {
        return;
      }

      isProcessingCommandRef.current =
        true;

      shouldSendCommandRef.current =
        false;

      if (
        commandFinishTimerRef.current
      ) {
        clearTimeout(
          commandFinishTimerRef.current
        );

        commandFinishTimerRef.current =
          null;
      }

      // ==========================================
      // Keep command mode TRUE while requestData()
      // is flushing the final audio chunk.
      // ==========================================

      try {
        await flushRecorderData();
      } catch (error) {
        console.warn(
          "⚠️ Failed to flush recorder data:",
          error
        );
      }

      // ==========================================
      // Snapshot final command
      // ==========================================

      const chunks = [
        ...commandAudioChunksRef.current,
      ];

      commandAudioChunksRef.current =
        [];

      const commandStartedAt =
        commandStartedAtRef.current;

      const commandDuration =
        commandStartedAt
          ? Date.now() -
          commandStartedAt
          : 0;

      // NOW leave command mode
      isCommandModeRef.current =
        false;

      commandHasAnySpeechRef.current =
        false;

      commandHasFinalResultRef.current =
        false;

      setIsListening(true);
      setIsThinking(true);

      console.log(
        "🛑 Finishing command:",
        {
          chunks:
            chunks.length,

          commandDuration:
            `${(
              commandDuration / 1000
            ).toFixed(2)} sec`,
        }
      );

      // ==========================================
      // Need WebM initialization chunk
      // ==========================================

      const firstChunk =
        firstAudioChunkRef.current;

      if (!firstChunk) {
        console.warn(
          "⚠️ WebM initialization chunk is missing"
        );

        setIsThinking(false);
        setIsListening(false);

        isProcessingCommandRef.current =
          false;

        commandStartedAtRef.current =
          null;

        clearPreRollBuffer();

        return;
      }

      // ==========================================
      // Build final WebM
      //
      // FIRST INIT CHUNK
      // +
      // LAST 10 SECONDS
      // +
      // COMMAND AUDIO
      // ==========================================

      const audioParts = [
        firstChunk,
      ];

      for (
        const chunk of chunks
      ) {
        if (
          chunk?.data &&
          chunk.data.size > 0
        ) {
          audioParts.push(
            chunk.data
          );
        }
      }

      const audioBlob =
        new Blob(
          audioParts,
          {
            type:
              firstChunk.type ||
              "audio/webm;codecs=opus",
          }
        );

      if (!audioBlob.size) {
        console.warn(
          "⚠️ Final audio blob is empty"
        );

        setIsThinking(false);
        setIsListening(false);
        
        isProcessingCommandRef.current =
          false;

        commandStartedAtRef.current =
          null;

        clearPreRollBuffer();

        return;
      }

      console.log(
        "✅ Final WebM created:",
        {
          size:
            audioBlob.size,

          chunks:
            audioParts.length,

          type:
            audioBlob.type,
        }
      );

      // ==========================================
      // PLAY USER AUDIO FIRST
      // ==========================================

      assistantSpeakingRef.current =
        true;

      // try {
      //   await playAudioBlob(
      //     audioBlob
      //   );
      // } catch (error) {
      //   console.warn(
      //     "⚠️ User audio playback failed:",
      //     error
      //   );
      // }

      // ==========================================
      // SEND TO AI
      // ==========================================

      try {
        const activeSessionId =
          sessionIdRef.current ||
          createUuid();

        console.log(
          "📤 Sending voice to AI...",
          {
            sessionId:
              activeSessionId,

            subscriberId:
              subscriberIdRef.current,

            organizationId:
              organizationIdRef.current,

            language:
              langRef.current,

            commandDuration:
              `${(
                commandDuration / 1000
              ).toFixed(2)} sec`,

            preRoll:
              "10 sec",

            size:
              audioBlob.size,

            type:
              audioBlob.type,
          }
        );

        const response =
          await sendAssistantVoiceChatStreamMessage({
            audioBlob,

            sessionId:
              activeSessionId,

            subscriberId:
              subscriberIdRef.current,

            organizationId:
              organizationIdRef.current,

            language:
              langRef.current,

            voiceReply:
              true,

            voice:
              "nova",
          });

        setIsThinking(false);
        setIsListening(false);

        console.log(
          "🤖 AI response:",
          response
        );

        // ========================================
        // UPDATE SESSION
        // ========================================

        const nextSessionId =
          normalizeChatSessionId(
            response.sessionId
          ) ||
          activeSessionId;

        sessionIdRef.current =
          nextSessionId;

        setConversation(
          (prev) => ({
            ...prev,

            key:
              conversationKeyRef.current,

            sessionId:
              nextSessionId,
          })
        );

        // ========================================
        // PLAY SAMA RESPONSE
        // ========================================

        if (
          response.audioSource
        ) {
          console.log(
            "🔊 Playing SAMA voice response..."
          );

          assistantSpeakingRef.current =
            true;

          try {
            const replyAudio =
              new window.Audio(
                response.audioSource
              );

            replyAudio.volume =
              1;

            await replyAudio.play();

            console.log(
              "🔊 SAMA voice finished"
            );
          } catch (audioError) {
            console.warn(
              "⚠️ Could not autoplay SAMA voice:",
              audioError
            );
          } finally {
            assistantSpeakingRef.current =
              false;
          }
        } else {
          console.warn(
            "⚠️ AI response has no audioSource"
          );
        }
      } catch (error) {

        setIsThinking(false);
        setIsListening(false);

        console.error(
          "❌ Voice command API error:",
          error
        );
      } finally {
        // ========================================
        // Reset command state
        // ========================================

        commandStartedAtRef.current =
          null;

        commandAudioChunksRef.current =
          [];

        isCommandModeRef.current =
          false;

        isProcessingCommandRef.current =
          false;

        commandHasAnySpeechRef.current =
          false;

        commandHasFinalResultRef.current =
          false;

        assistantSpeakingRef.current =
          false;

        // ========================================
        // IMPORTANT:
        // Clear stale pre-roll.
        // New clean pre-roll starts now.
        // ========================================

        clearPreRollBuffer();

        // ========================================
        // NO WAKE RESTART HERE
        //
        // The same SpeechRecognition instance
        // has been alive during the whole command.
        // ========================================

        if (
          shouldRunRef.current
        ) {
          console.log(
            "✅ Command finished - wake recognition remains active"
          );
        }
      }
    };

  // =========================================================
  // Start command mode
  // =========================================================

  const startCommandRecording =
    () => {
      if (
        !shouldRunRef.current
      ) {
        return false;
      }

      if (
        !continuousRecorderRef.current ||
        continuousRecorderRef.current.state !==
        "recording"
      ) {
        console.warn(
          "⚠️ Continuous MediaRecorder is not running"
        );

        return false;
      }

      // ==========================================
      // Snapshot last 10 seconds BEFORE entering
      // command mode.
      // ==========================================

      const preRollChunks =
        getPreRollChunks();

      commandAudioChunksRef.current =
        [...preRollChunks];

      commandStartedAtRef.current =
        Date.now();

      commandHasAnySpeechRef.current =
        false;

      commandHasFinalResultRef.current =
        false;

      isCommandModeRef.current =
        true;

      shouldSendCommandRef.current =
        true;

      isProcessingCommandRef.current =
        false;

      speechBufferRef.current =
        "";

      setIsListening(true);

      console.log(
        "🎤 Command mode started:",
        {
          preRollChunks:
            preRollChunks.length,

          preRollSeconds:
            PRE_ROLL_MS / 1000,
        }
      );

      return true;
    };

  // =========================================================
  // Trigger wake word
  // =========================================================

  const trigger = () => {
    const now =
      Date.now();

    // Small debounce
    if (
      now -
      lastTriggerRef.current <
      1500
    ) {
      return;
    }

    if (
      isCommandModeRef.current ||
      isProcessingCommandRef.current
    ) {
      return;
    }

    if (
      !shouldRunRef.current
    ) {
      return;
    }

    lastTriggerRef.current =
      now;

    console.log(
      "🔥 HI SAMA DETECTED"
    );

    const started =
      startCommandRecording();

    if (!started) {
      console.warn(
        "⚠️ Could not start command mode because recorder is not ready"
      );

      return;
    }

    // ========================================================
    // IMPORTANT:
    // DO NOT stop or abort wake recognition.
    //
    // It stays alive continuously and simply changes behavior
    // because isCommandModeRef.current === true.
    // ========================================================
  };

  // =========================================================
  // MAIN EFFECT
  // =========================================================

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn(
        "❌ Speech Recognition is not supported"
      );

      return;
    }

    console.log(
      "🎙️ Voice Assistant mounted - waiting for START"
    );

    // =======================================================
    // Create ONE persistent wake recognition
    // =======================================================

    const recognition =
      new SpeechRecognition();

    recognitionRef.current =
      recognition;

    recognition.lang =
      "en-US";

    recognition.continuous =
      true;

    recognition.interimResults =
      true;

    recognition.maxAlternatives =
      3;

    // =======================================================
    // Start recognition
    // =======================================================

    const startRecognition =
      () => {
        if (
          !shouldRunRef.current
        ) {
          return;
        }

        if (
          isRunningRef.current
        ) {
          return;
        }

        try {
          console.log(
            "▶️ Starting wake word recognition..."
          );

          recognition.start();
        } catch (error) {
          console.warn(
            "⚠️ Wake recognition start failed:",
            error
          );

          isRunningRef.current =
            false;
        }
      };

    // =======================================================
    // Reliable restart
    // =======================================================

    const restartWakeRecognition =
      () => {
        if (
          !shouldRunRef.current
        ) {
          return;
        }

        if (
          isRunningRef.current
        ) {
          return;
        }

        if (
          wakeRestartTimerRef.current
        ) {
          return;
        }

        wakeRestartTimerRef.current =
          setTimeout(() => {
            wakeRestartTimerRef.current =
              null;

            if (
              !shouldRunRef.current
            ) {
              return;
            }

            if (
              isRunningRef.current
            ) {
              return;
            }

            try {
              console.log(
                "🔄 Restarting wake word recognition..."
              );

              recognition.start();
            } catch (error) {
              console.warn(
                "⚠️ Wake recognition restart failed:",
                error
              );

              isRunningRef.current =
                false;

              if (
                wakeRetryTimerRef.current
              ) {
                clearTimeout(
                  wakeRetryTimerRef.current
                );
              }

              wakeRetryTimerRef.current =
                setTimeout(() => {
                  wakeRetryTimerRef.current =
                    null;

                  restartWakeRecognition();
                }, WAKE_RETRY_DELAY_MS);
            }
          }, WAKE_RESTART_DELAY_MS);
      };

    restartWakeRecognitionRef.current =
      restartWakeRecognition;

    // =======================================================
    // START CONTROLLER
    // =======================================================

    const startVoiceAssistant =
      async () => {
        console.log(
          "▶️ START Voice Assistant"
        );

        shouldRunRef.current =
          true;

        isProcessingCommandRef.current =
          false;

        isCommandModeRef.current =
          false;

        shouldSendCommandRef.current =
          false;

        speechBufferRef.current =
          "";

        clearPreRollBuffer();

        // ==============================================
        // Start recorder first
        // ==============================================

        const recordingStarted =
          await startContinuousRecording();

        if (
          !recordingStarted
        ) {
          console.warn(
            "⚠️ Continuous recording could not start"
          );

          // Don't permanently disable assistant.
          // Retry while it is enabled.

          setTimeout(() => {
            if (
              shouldRunRef.current &&
              !continuousRecorderRef.current
            ) {
              startContinuousRecording();
            }
          }, 700);
        }

        // ==============================================
        // Start persistent recognition
        // ==============================================

        if (
          shouldRunRef.current &&
          !isRunningRef.current
        ) {
          startRecognition();
        }
      };

    // =======================================================
    // STOP CONTROLLER
    // =======================================================

    const stopVoiceAssistant =
      () => {
        console.log(
          "🛑 STOP Voice Assistant"
        );

        shouldRunRef.current =
          false;

        isRunningRef.current =
          false;

        isCommandModeRef.current =
          false;

        isProcessingCommandRef.current =
          false;

        shouldSendCommandRef.current =
          false;

        commandHasAnySpeechRef.current =
          false;

        commandHasFinalResultRef.current =
          false;

        speechBufferRef.current =
          "";

        assistantSpeakingRef.current =
          false;

        commandStartedAtRef.current =
          null;

        if (
          commandFinishTimerRef.current
        ) {
          clearTimeout(
            commandFinishTimerRef.current
          );

          commandFinishTimerRef.current =
            null;
        }

        // ==============================================
        // Save disabled state
        // ==============================================

        try {
          window.localStorage.setItem(
            BROWSER_VOICE_STORAGE_KEY,
            JSON.stringify({
              enabled:
                false,
            })
          );
        } catch (error) {
          console.error(
            "Failed to save browser voice disabled state:",
            error
          );
        }

        // ==============================================
        // Clear restart timers
        // ==============================================

        if (
          wakeRestartTimerRef.current
        ) {
          clearTimeout(
            wakeRestartTimerRef.current
          );

          wakeRestartTimerRef.current =
            null;
        }

        if (
          wakeRetryTimerRef.current
        ) {
          clearTimeout(
            wakeRetryTimerRef.current
          );

          wakeRetryTimerRef.current =
            null;
        }

        // ==============================================
        // Stop recognition
        // ==============================================

        try {
          recognition.abort();
        } catch { }

        try {
          recognition.stop();
        } catch { }

        recognitionRef.current =
          null;

        // ==============================================
        // Stop recorder
        // ==============================================

        stopContinuousRecording();

        // ==============================================
        // Clear buffers
        // ==============================================

        commandAudioChunksRef.current =
          [];

        clearPreRollBuffer();

        setIsListening(false);

        console.log(
          "✅ Voice Assistant FULLY STOPPED"
        );
      };

    // =======================================================
    // Recognition START
    // =======================================================

    recognition.onstart =
      () => {
        isRunningRef.current =
          true;

        console.log(
          "🎤 Wake recognition started"
        );
      };

    // =======================================================
    // Recognition RESULT
    // =======================================================

    recognition.onresult =
      (event) => {
        if (
          !shouldRunRef.current ||
          isProcessingCommandRef.current
        ) {
          return;
        }

        let eventText =
          "";

        for (
          let i =
            event.resultIndex;
          i <
          event.results.length;
          i++
        ) {
          const result =
            event.results[i];

          if (!result) {
            continue;
          }

          const transcript =
            result[0]?.transcript?.trim();

          if (!transcript) {
            continue;
          }

          // ==========================================
          // COMMAND MODE
          // ==========================================

          if (
            isCommandModeRef.current
          ) {
            commandHasAnySpeechRef.current =
              true;

            console.log(
              `🗣️ Command [${result.isFinal
                ? "FINAL"
                : "INTERIM"
              }]:`,
              transcript
            );

            if (
              result.isFinal
            ) {
              commandHasFinalResultRef.current =
                true;

              console.log(
                "✅ Final command received:",
                transcript
              );

              scheduleCommandFinish();
            }

            continue;
          }

          // ==========================================
          // WAKE MODE
          // ==========================================

          console.log(
            `👂 Heard [${result.isFinal
              ? "FINAL"
              : "INTERIM"
            }]:`,
            transcript
          );

          eventText +=
            ` ${transcript}`;
        }


        if (
          isCommandModeRef.current
        ) {
          return;
        }

        const text =
          normalize(
            eventText
          );

        if (!text) {
          return;
        }

        speechBufferRef.current =
          normalize(
            `${speechBufferRef.current} ${text}`
          );

        const words =
          speechBufferRef.current.split(
            " "
          );

        if (
          words.length > 12
        ) {
          speechBufferRef.current =
            words
              .slice(-12)
              .join(" ");
        }

        console.log(
          "🧠 Wake buffer:",
          speechBufferRef.current
        );

        if (
          detectWakeWord(
            speechBufferRef.current
          )
        ) {
          trigger();
        }
      };

    recognition.onerror =
      (event) => {
        console.warn(
          "⚠️ Recognition error:",
          event.error
        );

        isRunningRef.current =
          false;

        if (
          event.error ===
          "not-allowed" ||
          event.error ===
          "service-not-allowed"
        ) {
          console.error(
            "❌ Microphone permission denied"
          );

          shouldRunRef.current =
            false;

          return;
        }

        if (
          event.error ===
          "audio-capture"
        ) {
          console.warn(
            "🎙️ Audio capture problem"
          );
        }

        if (
          event.error ===
          "network"
        ) {
          console.warn(
            "🌐 Speech recognition network error"
          );
        }

        if (
          event.error ===
          "aborted"
        ) {
          console.log(
            "🛑 Recognition aborted"
          );

          if (
            !shouldRunRef.current
          ) {
            return;
          }
        }

        if (
          shouldRunRef.current
        ) {
          restartWakeRecognition();
        }
      };

    recognition.onend =
      () => {
        console.log(
          "🛑 Wake recognition ended"
        );

        isRunningRef.current =
          false;

        if (
          !shouldRunRef.current
        ) {
          return;
        }

        if (
          !isProcessingCommandRef.current
        ) {
          restartWakeRecognition();
        }
      };


    const watchdog =
      setInterval(() => {
        if (
          !shouldRunRef.current
        ) {
          return;
        }

        // ==========================================
        // Recorder health
        // ==========================================

        if (
          !continuousRecorderRef.current ||
          continuousRecorderRef.current.state !==
          "recording"
        ) {
          console.warn(
            "🐕 Watchdog: MediaRecorder is not running"
          );

          startContinuousRecording();
        }

        // ==========================================
        // Recognition health
        // ==========================================

        if (
          !isRunningRef.current &&
          !isProcessingCommandRef.current
        ) {
          console.warn(
            "🐕 Watchdog: wake recognition is not running"
          );

          restartWakeRecognition();
        }
      }, 2500);

    // =======================================================
    // GLOBAL CONTROLLER
    // =======================================================

    const savedEnabled =
      readBrowserVoiceEnabled();

    shouldRunRef.current =
      savedEnabled;

    window.__samaVoiceAssistant = {
      start:
        startVoiceAssistant,

      stop:
        stopVoiceAssistant,
    };

    console.log(
      "✅ Voice Assistant controller registered",
      {
        enabled:
          savedEnabled,
      }
    );

    // =======================================================
    // AUTO START
    //
    // Small delay prevents React development-mode
    // mount/cleanup from racing with getUserMedia().
    // =======================================================

    let autoStartCancelled =
      false;

    if (
      savedEnabled
    ) {
      setTimeout(() => {
        if (
          autoStartCancelled
        ) {
          return;
        }

        if (
          shouldRunRef.current
        ) {
          console.log(
            "🔄 Browser voice was enabled - starting automatically..."
          );

          startVoiceAssistant();
        }
      }, 100);
    }

    // =======================================================
    // CLEANUP
    // =======================================================

    return () => {
      console.log(
        "🧹 Cleaning Voice Assistant"
      );

      autoStartCancelled =
        true;

      if (
        window.__samaVoiceAssistant
      ) {
        delete window.__samaVoiceAssistant;
      }

      shouldRunRef.current =
        false;

      isRunningRef.current =
        false;

      isCommandModeRef.current =
        false;

      isProcessingCommandRef.current =
        false;

      shouldSendCommandRef.current =
        false;

      assistantSpeakingRef.current =
        false;

      if (
        commandFinishTimerRef.current
      ) {
        clearTimeout(
          commandFinishTimerRef.current
        );

        commandFinishTimerRef.current =
          null;
      }

      clearInterval(
        watchdog
      );

      if (
        wakeRestartTimerRef.current
      ) {
        clearTimeout(
          wakeRestartTimerRef.current
        );

        wakeRestartTimerRef.current =
          null;
      }

      if (
        wakeRetryTimerRef.current
      ) {
        clearTimeout(
          wakeRetryTimerRef.current
        );

        wakeRetryTimerRef.current =
          null;
      }

      // ==============================================
      // Stop recognition
      // ==============================================

      try {
        recognition.abort();
      } catch { }

      try {
        recognition.stop();
      } catch { }

      recognitionRef.current =
        null;


      stopContinuousRecording();

      commandAudioChunksRef.current =
        [];

      clearPreRollBuffer();

      commandStartedAtRef.current =
        null;

      setIsListening(false);
    };
  }, []);

  return (
    <>
      {isListening && (
        <div className="browser-vc-overlay">
          <section
            className="browser-vc-card"
            aria-live="polite"
          >
            <button
              type="button"
              className="browser-vc-close"
              onClick={() => {
                setIsListening(false);
              }}
              aria-label="Close"
            >
              <CloseRoundedIcon />
            </button>

            <div
              className="browser-vc-orb"
              aria-hidden="true"
            >
              {isThinking ? (
                <PsychologyRoundedIcon />
              ) : (
                <HearingRoundedIcon />
              )}
            </div>

            <strong>
              {isThinking
                ? "Sama is Thinking..."
                : "Sama is Listening..."}
            </strong>
          </section>
        </div>
      )}
    </>
  );
}

